import axios from "axios";
import { useConfig } from "providers/ConfigContext";
import { useEffect, useState } from "react";
import { checkSpsUd, getFairIdFromUrl } from "utils/HubUtility";

export type OrganizationData = {
	name: string;
	bookfairAccountId: string;
};

export type FairInfoData = {
	coaStatus: string;
	startDate: string;
	endDate: string;
};

export type ChairpersonData = {
	name: string;
};

export type FinancialFormData = {
	status: string;
	submitted: string;
	availableUntil: string;
};

export type CoChairData = {
	name: string;
}[];

export type EWalletData = {
	sales: string;
	unspentFunds: string;
	startDate: string;
	endDate: string;
	enabled: boolean;
};

export type OnlineShoppingData = {
	sales: string;
	startDate: string;
	endDate: string;
	enabled: boolean;
};

export type EventData = {
	scheduleDate: string;
	eventName: string;
};

export type HomepageData = {
	fairUrl: string;
	suppressFairFinder: boolean;
	events: EventData[];
};

export type AnalyticsData = {
	logins: number;
	homePageVisits: number;
};

const defaultOrganizationData: OrganizationData = {
	name: "",
	bookfairAccountId: "",
};

const defaultFairInfoData: FairInfoData = {
	coaStatus: "",
	startDate: "",
	endDate: "",
};

const defaultChairpersonData: ChairpersonData = {
	name: "",
};

const defaultFinancialFormData: FinancialFormData = {
	status: "",
	submitted: "",
	availableUntil: "",
};

const defaultCoChairData: CoChairData = [
	{
		name: "",
	},
];

const defaultEWalletData: EWalletData = {
	sales: "",
	unspentFunds: "",
	startDate: "",
	endDate: "",
	enabled: false,
};

const defaultOnlineShoppingData: OnlineShoppingData = {
	sales: "",
	startDate: "",
	endDate: "",
	enabled: false,
};

const defaultEventData: EventData = {
	scheduleDate: "",
	eventName: "",
};

const defaultHomepageData: HomepageData = {
	fairUrl: "",
	suppressFairFinder: false,
	events: [defaultEventData],
};

const defaultAnalyticsData: AnalyticsData = {
	logins: 0,
	homePageVisits: 0,
};

const useConsultationDashboardData = () => {
	const { bookfairsAPI } = useConfig();

	const [organizationData, setOrganizationData] = useState<OrganizationData>(
		defaultOrganizationData
	);
	const [fairInfoData, setFairInfoData] =
		useState<FairInfoData>(defaultFairInfoData);
	const [chairpersonData, setChairpersonData] = useState<ChairpersonData>(
		defaultChairpersonData
	);
	const [financialFormData, setFinancialFormData] =
		useState<FinancialFormData>(defaultFinancialFormData);
	const [coChairData, setCoChairData] =
		useState<CoChairData>(defaultCoChairData);
	const [eWalletData, setEWalletData] =
		useState<EWalletData>(defaultEWalletData);
	const [onlineShoppingData, setOnlineShoppingData] =
		useState<OnlineShoppingData>(defaultOnlineShoppingData);
	const [homepageData, setHomepageData] =
		useState<HomepageData>(defaultHomepageData);
	const [analyticsData, setAnalyticsData] =
		useState<AnalyticsData>(defaultAnalyticsData);

	const [fairId, setFairId] = useState<string>("");

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isError, setIsError] = useState<boolean>(false);

	const handleErrorRouting = () => {
		window.open(`/content/fairs/hub/error.html`, "_top");
	};

	useEffect(() => {
		const fetchConsultationDashboardData = async () => {
			const currentFair = getFairIdFromUrl();
			if (currentFair === "current") {
				handleErrorRouting();
				return;
			}
			if (checkSpsUd()) {
				try {
					await axios
						.get(
							`${bookfairsAPI}/user/fairs/${currentFair}/consultant-dashboard`,
							{
								withCredentials: true,
							}
						)
						.then((response) => {
							if (response.status === 200) {
								if (response.data) {
									setOrganizationData(
										response.data.organization
									);
									setFairInfoData(response.data.fairInfo);
									setChairpersonData(
										response.data.chairperson
									);
									setFinancialFormData(
										response.data.financialForm
									);
									setCoChairData(response.data["co-chairs"]);
									setEWalletData(response.data.ewallet);
									setOnlineShoppingData(
										response.data.onlineShopping
									);
									setHomepageData(response.data.homepage);
									setAnalyticsData(response.data.analytics);
									setFairId(
										response.headers[
											"sbf-jarvis-resource-id"
										]
									);
								}
							} else {
								handleErrorRouting();
							}
						})
						.catch((err) => {
							setIsError(true);
							handleErrorRouting();
						});
				} catch (err: any) {
					setIsError(true);
					handleErrorRouting();
				} finally {
					setIsLoading(false);
				}
			}
		};
		fetchConsultationDashboardData();
	}, [bookfairsAPI]);
	return {
		isLoading,
		isError,
		organizationData,
		fairInfoData,
		chairpersonData,
		financialFormData,
		coChairData,
		eWalletData,
		onlineShoppingData,
		homepageData,
		analyticsData,
		fairId,
	};
};

export default useConsultationDashboardData;
